import { useRef, useState, useEffect, useCallback } from "react";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { AdSlot } from "@/components/ads/AdSlot";
import { COLOR_LIST, type ColorResearchSource } from "@/data/colors";

// ── Paint color type ───────────────────────────────────────────────────────────
type PaintColor = { name: string; hex: string; psychology: string; researchSources?: ColorResearchSource[] };

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[character]!);
}

// ── Grouped paint colors ───────────────────────────────────────────────────────
const COLOR_GROUPS: Record<string, PaintColor[]> = {
  "Whites & Creams": [
    { name: "Pure White",     hex: "#FAFAFA", psychology: "Creates a sense of spaciousness and purity — reflects light maximally for bright, airy rooms." },
    { name: "Warm Ivory",     hex: "#FFF8E7", psychology: "Softens a space with gentle warmth without the starkness of pure white — welcoming in any room." },
    { name: "Swiss Coffee",   hex: "#F5EFE6", psychology: "A timeless off-white with subtle warmth — pairs with almost any accent and never feels clinical." },
    { name: "Antique White",  hex: "#F8F2E9", psychology: "Evokes old-world elegance and calm sophistication — ideal for traditional and farmhouse styles." },
    { name: "Linen",          hex: "#FAF0E6", psychology: "Brings a soft, tactile warmth to walls — calming and organic, never stark." },
    { name: "Oyster",         hex: "#F2EDE5", psychology: "A whisper of warmth between white and beige — grounded yet luminous in natural light." },
    { name: "Cool White",     hex: "#EFF3F5", psychology: "Crisp and contemporary — adds a modern, clean freshness especially in north-facing rooms." },
    { name: "Cream",          hex: "#FFFDD0", psychology: "Soft and nurturing; adds gentle coziness without darkening the room." },
  ],
  "Grays & Neutrals": [
    { name: "Pale Frost",     hex: "#EAEAEA", psychology: "Light and airy; a versatile near-white gray that adapts to any palette." },
    { name: "Silver Birch",   hex: "#C8C8C8", psychology: "Clean and modern — neutral enough to recede while adding quiet definition." },
    { name: "Greige",         hex: "#C4B89A", psychology: "The perfect gray-beige blend; warm enough to feel cozy, cool enough to feel current." },
    { name: "Dove Gray",      hex: "#B0A8A0", psychology: "Warm and elegant — a sophisticated neutral that flatters almost every room style." },
    { name: "Stone",          hex: "#928F85", psychology: "Earthy and grounded; brings the permanence and reliability of natural stone indoors." },
    { name: "Storm Gray",     hex: "#6B7280", psychology: "Moody and purposeful — adds drama and depth, especially beautiful in larger rooms." },
    { name: "Slate",          hex: "#708090", psychology: "Cool and composed; suggests clarity and quiet authority in home offices." },
    { name: "Graphite",       hex: "#4A4A4A", psychology: "Bold and commanding — creates a dramatic anchor-wall effect with sophisticated edge." },
  ],
  "Blues": [
    { name: "Powder Blue",    hex: "#B0D4E8", psychology: "Soft and sky-like; promotes calm and open breathing — lovely in bathrooms and bedrooms." },
    { name: "Sky Blue",       hex: "#87CEEB", psychology: "Bright and optimistic — opens up a room and lifts mood on grey days." },
    { name: "Cerulean",       hex: "#4A8FB5", psychology: "Deep sky energy; focused and calming — great for creative home offices." },
    { name: "Denim Blue",     hex: "#5B7FA3", psychology: "Familiar and dependable — brings relaxed confidence, perfect for living rooms." },
    { name: "Classic Blue",   hex: "#4472C4", psychology: "Timeless and trustworthy; one of the most universally calming colors in interior design." },
    { name: "French Blue",    hex: "#4E6B9A", psychology: "Refined and sophisticated — adds a quiet European elegance to any space." },
    { name: "Focus Navy",     hex: "#2C3E6B", psychology: "Sharpens concentration and deepens thought — perfect for home offices and reading rooms." },
    { name: "Midnight Blue",  hex: "#191970", psychology: "Deeply immersive; creates a cocooning effect ideal for dens and media rooms." },
  ],
  "Greens": [
    { name: "Mint Cream",     hex: "#F0FFF4", psychology: "Fresh and revitalising — barely-there green that breathes life into any room." },
    { name: "Seafoam",        hex: "#93E9BE", psychology: "Cool and aquatic; relaxing and restorative, perfect for bathrooms and sunrooms." },
    { name: "Calming Sage",   hex: "#87A987", psychology: "Reduces stress and fosters inner balance — an ideal choice for bedrooms and yoga spaces." },
    { name: "Eucalyptus",     hex: "#5C8A6D", psychology: "Grounding and healing — brings the clarity of nature's medicine cabinet into the home." },
    { name: "Moss Green",     hex: "#8A9A5B", psychology: "Earthy and honest; promotes a quiet, settled feeling in dining and living rooms." },
    { name: "Forest Moss",    hex: "#4A6741", psychology: "Grounds and restores focus; brings restorative nature indoors to reduce mental fatigue." },
    { name: "Hunter Green",   hex: "#355E3B", psychology: "Rich and commanding; classic and reliable — ideal for libraries and statement walls." },
    { name: "Deep Emerald",   hex: "#1B6B3A", psychology: "Lush and vital; the most growth-positive color — energising in kitchens and studies." },
  ],
  "Warm Tones": [
    { name: "Peach Blush",    hex: "#FFDAB9", psychology: "Nurturing and gentle — fills a room with soft warmth, ideal for nurseries and bedrooms." },
    { name: "Apricot",        hex: "#FBCEB1", psychology: "Cheerful and inviting; stimulates gentle joy without overpowering smaller spaces." },
    { name: "Sandy Beige",    hex: "#E8C99A", psychology: "Grounded and sun-warmed — a classic neutral that makes rooms feel naturally lit." },
    { name: "Warm Ochre",     hex: "#CC8B3C", psychology: "Radiates optimism and creative energy — uplifting in kitchens, studios, and living rooms." },
    { name: "Terracotta",     hex: "#C4704A", psychology: "Stirs conversation and appetite with earthy warmth — energising in dining rooms." },
    { name: "Burnt Orange",   hex: "#CC5500", psychology: "Bold and energetic — stimulates social energy and creativity in gathering spaces." },
    { name: "Rust",           hex: "#B7410E", psychology: "Warm, aged, and sophisticated — brings artisanal depth to accent walls and kitchens." },
    { name: "Brick Red",      hex: "#CB4154", psychology: "Passionate and grounding; earthy red tones stimulate appetite and lively conversation." },
  ],
  "Pinks & Purples": [
    { name: "Blush",          hex: "#FFE4E1", psychology: "The most serene of pinks — barely-there warmth that soothes without sweetness overload." },
    { name: "Rose Quartz",    hex: "#F7CAC9", psychology: "Gentle and compassionate; promotes emotional openness — wonderful in bedrooms." },
    { name: "Blush Rose",     hex: "#E8A0B0", psychology: "Softens the atmosphere and nurtures empathy — a gentle wrap for nurseries and bedrooms." },
    { name: "Dusty Rose",     hex: "#DCAE96", psychology: "Mature, earthy pink; sophisticated warmth that pairs beautifully with neutrals." },
    { name: "Dusty Mauve",    hex: "#A87C8A", psychology: "Encourages introspection and romance — a sophisticated choice for master bedrooms." },
    { name: "Soft Lavender",  hex: "#B8A9CC", psychology: "Invites creativity and quiet luxury — calming for reading nooks and creative spaces." },
    { name: "Lilac",          hex: "#C8A2C8", psychology: "Playful yet calming; stimulates imagination and a sense of whimsy in creative spaces." },
    { name: "Deep Plum",      hex: "#8E4585", psychology: "Rich and mysterious; adds dramatic luxury and depth to accent walls and dining rooms." },
  ],
  "Browns & Earth": [
    { name: "Sand",           hex: "#D4B896", psychology: "Warm and dependable — the colour of beaches and desert light; grounding and open." },
    { name: "Latte",          hex: "#C8A882", psychology: "Cozy and familiar; wraps a room in café warmth ideal for living areas." },
    { name: "Butter Cream",   hex: "#F5E6C0", psychology: "Spreads gentle warmth and comfort — a welcoming tone for hallways and kitchens." },
    { name: "Caramel",        hex: "#C68642", psychology: "Rich and comforting — brings golden warmth reminiscent of autumn afternoons." },
    { name: "Warm Brown",     hex: "#8B6347", psychology: "Steady and nourishing; the quintessential earthy neutral for any gathering space." },
    { name: "Walnut",         hex: "#6B4423", psychology: "Deep and reliable — the colour of quality craftsmanship; pairs with metallics and greens." },
    { name: "Chocolate",      hex: "#4A2C17", psychology: "Deeply satisfying and indulgent; creates intimate, cocoon-like dens and bedrooms." },
    { name: "Terracotta Clay",hex: "#CC7722", psychology: "Sun-baked warmth from Mediterranean architecture; energetic yet grounded in nature." },
  ],
};

// Flat list of all grouped colors for quick lookup
const ALL_GROUPED_COLORS: PaintColor[] = Object.values(COLOR_GROUPS).flat();

const CATEGORY_KEYS = Object.keys(COLOR_GROUPS);
const DEFAULT_COLOR = COLOR_GROUPS["Whites & Creams"][0];

// ── Color math helpers ─────────────────────────────────────────────────────────
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
}

function colorDist(r1:number,g1:number,b1:number,r2:number,g2:number,b2:number) {
  return Math.sqrt((r1-r2)**2+(g1-g2)**2+(b1-b2)**2);
}

// Blend paint onto a pixel, preserving original brightness (shadow/highlight structure)
function blendPixel(
  or:number, og:number, ob:number,   // original pixel
  sr:number, sg:number, sb:number,   // sampled wall colour
  pr:number, pg:number, pb:number,   // paint colour
  strength: number                    // 0-1 blend strength
): [number,number,number] {
  const origLum = 0.299*or + 0.587*og + 0.114*ob;
  const sampLum = 0.299*sr + 0.587*sg + 0.114*sb || 1;
  const ratio = origLum / sampLum;
  const nr = Math.min(255, pr * ratio);
  const ng = Math.min(255, pg * ratio);
  const nb = Math.min(255, pb * ratio);
  return [
    Math.round(or + (nr - or) * strength),
    Math.round(og + (ng - og) * strength),
    Math.round(ob + (nb - ob) * strength),
  ];
}

// ── Calculator defaults ────────────────────────────────────────────────────────
const CALC_DEFAULT = { length: "", width: "", height: "", doors: "1", windows: "2", coats: "2" };
const SQFT_PER_GALLON = 350;

type CalcInputs = typeof CALC_DEFAULT;

function computeCalc(c: CalcInputs, pricePerGal: number) {
  const l = parseFloat(c.length)||0, w = parseFloat(c.width)||0, h = parseFloat(c.height)||0;
  const d = parseInt(c.doors)||0, win = parseInt(c.windows)||0, coats = parseInt(c.coats)||1;
  const gross = 2*(l+w)*h;
  const net   = Math.max(0, gross - (d*20 + win*15));
  const gals  = Math.ceil(net * coats / SQFT_PER_GALLON);
  const cost  = gals * pricePerGal;
  return { gross: Math.round(gross), net: Math.round(net), gals, cost };
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function PaintVisualizer() {
  useSeo({
    title: "Live Room Paint Visualizer & Calculator | Mychromamind",
    description:
      "Visualize any paint color on your walls in real-time using your camera or a photo upload. Includes color psychology insights and an integrated paint quantity & cost calculator.",
    path: "/paint-visualizer",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          name: "Live Room Paint Visualizer & Calculator",
          applicationCategory: "DesignApplication",
          operatingSystem: "Web",
          description:
            "Client-side paint color visualizer with live webcam or photo upload, color psychology breakdowns, and an integrated paint quantity calculator. 100% browser-based.",
          url: "https://mychromamind.com/paint-visualizer",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",    item: "https://mychromamind.com" },
            { "@type": "ListItem", position: 2, name: "Paint Visualizer", item: "https://mychromamind.com/paint-visualizer" },
          ],
        },
      ],
    },
  });

  // ── State ──────────────────────────────────────────────────────────────────
  const [mode, setMode]           = useState<"camera"|"upload">("upload");
  const [paintHex, setPaintHex]   = useState(DEFAULT_COLOR.hex);
  const [activeName, setActiveName]         = useState(DEFAULT_COLOR.name);
  const [activePsychology, setActivePsychology] = useState(DEFAULT_COLOR.psychology);
  const [activeResearchSources, setActiveResearchSources] = useState<ColorResearchSource[]>([]);
  const [colorCategory, setColorCategory]   = useState(CATEGORY_KEYS[0]);
  const [libSearch, setLibSearch]           = useState("");
  const [libOpen, setLibOpen]               = useState(false);
  const [sampledRgb, setSampledRgb] = useState<[number,number,number]|null>(null);
  const [tolerance, setTolerance] = useState(45);
  const [strength, setStrength]   = useState(85);
  const [hasImage, setHasImage]   = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError]   = useState("");
  const [calcOpen, setCalcOpen]     = useState(false);
  const [calcInputs, setCalcInputs] = useState<CalcInputs>(CALC_DEFAULT);
  const [pricePerGallon, setPricePerGallon] = useState("55");
  const [hint, setHint]           = useState("Upload a photo or start your camera, then click a wall to apply paint.");
  const [popupBlocked, setPopupBlocked] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const offscreenRef = useRef<HTMLCanvasElement|null>(null); // holds original pixels
  const videoRef     = useRef<HTMLVideoElement>(null);
  const streamRef    = useRef<MediaStream|null>(null);
  const rafRef       = useRef<number>(0);
  const sampledRef   = useRef<[number,number,number]|null>(null);
  const paintHexRef  = useRef(paintHex);
  const toleranceRef = useRef(tolerance);
  const strengthRef  = useRef(strength);

  // Keep refs in sync
  useEffect(() => { paintHexRef.current = paintHex; }, [paintHex]);
  useEffect(() => { toleranceRef.current = tolerance; }, [tolerance]);
  useEffect(() => { strengthRef.current = strength; }, [strength]);

  // ── Core: apply paint mask to canvas ──────────────────────────────────────
  const applyMask = useCallback((src: ImageData, dest: CanvasRenderingContext2D) => {
    const sampled = sampledRef.current;
    if (!sampled) { dest.putImageData(src, 0, 0); return; }
    const [sr,sg,sb] = sampled;
    const [pr,pg,pb] = hexToRgb(paintHexRef.current);
    const tol  = toleranceRef.current * 2.55;  // 0-100 → 0-255 scale
    const str  = strengthRef.current / 100;
    const out  = new Uint8ClampedArray(src.data);
    for (let i = 0; i < out.length; i += 4) {
      const or = src.data[i], og = src.data[i+1], ob = src.data[i+2];
      if (colorDist(or,og,ob,sr,sg,sb) <= tol) {
        const [nr,ng,nb] = blendPixel(or,og,ob, sr,sg,sb, pr,pg,pb, str);
        out[i]=nr; out[i+1]=ng; out[i+2]=nb;
      }
    }
    dest.putImageData(new ImageData(out, src.width, src.height), 0, 0);
  }, []);

  // ── Re-apply whenever paint/tolerance/strength changes (image mode) ────────
  const redrawImage = useCallback(() => {
    const canvas = canvasRef.current;
    const off    = offscreenRef.current;
    if (!canvas || !off || !hasImage || cameraActive) return;
    const ctx = canvas.getContext("2d")!;
    const src = off.getContext("2d")!.getImageData(0, 0, off.width, off.height);
    applyMask(src, ctx);
  }, [hasImage, cameraActive, applyMask]);

  useEffect(() => { redrawImage(); }, [paintHex, tolerance, strength, sampledRgb, redrawImage]);

  // ── Camera loop ────────────────────────────────────────────────────────────
  const drawFrame = useCallback(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(drawFrame); return;
    }
    const ctx = canvas.getContext("2d")!;
    // Fit video into canvas
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width  = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    ctx.drawImage(video, 0, 0);
    if (sampledRef.current) {
      const src = ctx.getImageData(0, 0, canvas.width, canvas.height);
      applyMask(src, ctx);
    }
    rafRef.current = requestAnimationFrame(drawFrame);
  }, [applyMask]);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play();
      setCameraActive(true);
      setHasImage(false);
      setSampledRgb(null); sampledRef.current = null;
      setHint("Point your camera at a wall, then tap it to apply paint.");
      rafRef.current = requestAnimationFrame(drawFrame);
    } catch {
      setCameraError("Camera access denied. Please allow camera permission and try again.");
    }
  }, [drawFrame]);

  // Cleanup on unmount
  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  // Switch modes
  useEffect(() => {
    if (mode === "upload") {
      stopCamera();
    } else {
      setHasImage(false);
      const canvas = canvasRef.current;
      if (canvas) { const ctx = canvas.getContext("2d")!; ctx.clearRect(0,0,canvas.width,canvas.height); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ── Handle file upload ─────────────────────────────────────────────────────
  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    stopCamera();
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current!;
        const maxW = canvas.parentElement!.clientWidth || 700;
        const scale = Math.min(1, maxW / img.width);
        canvas.width  = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        // Store original in offscreen canvas
        const off = document.createElement("canvas");
        off.width = canvas.width; off.height = canvas.height;
        off.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        offscreenRef.current = off;
        // Draw to main canvas
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasImage(true);
        setSampledRgb(null); sampledRef.current = null;
        setHint("Great! Now tap a wall area to apply your paint color.");
      };
      img.src = e.target!.result as string;
    };
    reader.readAsDataURL(file);
  }, [stopCamera]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  // ── Handle canvas click → sample wall color ────────────────────────────────
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || (!hasImage && !cameraActive)) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);
    const ctx = canvas.getContext("2d")!;

    // For camera, sample from live frame (already drawn); for image sample from offscreen original
    let sourceCtx = ctx;
    if (!cameraActive && offscreenRef.current) {
      sourceCtx = offscreenRef.current.getContext("2d")!;
    }
    const px = sourceCtx.getImageData(x, y, 1, 1).data;
    const rgb: [number,number,number] = [px[0], px[1], px[2]];
    sampledRef.current = rgb;
    setSampledRgb(rgb);
    setHint("Wall sampled! Adjust tolerance or choose a different paint color.");
    if (!cameraActive) {
      const src = offscreenRef.current!.getContext("2d")!.getImageData(0, 0, canvas.width, canvas.height);
      applyMask(src, ctx);
    }
  }, [hasImage, cameraActive, applyMask]);

  // ── Color selection (any source) ──────────────────────────────────────────
  const selectColor = useCallback((c: PaintColor) => {
    setPaintHex(c.hex);
    setActiveName(c.name);
    setActivePsychology(c.psychology);
    setActiveResearchSources(c.researchSources ?? []);
  }, []);

  const priceNum = parseFloat(pricePerGallon) || 55;
  const calc = computeCalc(calcInputs, priceNum);
  const canCalc = !!(parseFloat(calcInputs.length) && parseFloat(calcInputs.width) && parseFloat(calcInputs.height));

  // ── Download painted canvas as PNG ────────────────────────────────────────
  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const filename = `room-${activeName.toLowerCase().replace(/\s+/g, "-")}.png`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so the browser has time to queue the download
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, "image/png");
  }, [activeName]);

  const handlePrint = () => {
    const { gross, net, gals, cost } = calc;
    const colorName = activeName;
    const psychText = activePsychology || "A custom color chosen for this room.";
    const sourceLinks = activeResearchSources.map((source) =>
      `<a href="${escapeHtml(source.url)}" target="_blank" rel="noreferrer">${escapeHtml(source.label)}</a>`
    ).join(" · ");
    const today         = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const doors         = parseInt(calcInputs.doors)  || 0;
    const windows       = parseInt(calcInputs.windows) || 0;
    const coats         = parseInt(calcInputs.coats)   || 2;
    const doorDeduct    = doors   * 20;
    const windowDeduct  = windows * 15;

    // Decide whether text on the paint swatch should be black or white
    const [pr, pg, pb] = hexToRgb(paintHex);
    const luminance = (0.299 * pr + 0.587 * pg + 0.114 * pb) / 255;
    const swatchText = luminance > 0.55 ? "#1a1a1a" : "#ffffff";

    const w = window.open("", "_blank");
    if (!w) { setPopupBlocked(true); return; }
    w.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Paint Report — ${colorName}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f5f4f0;color:#1a1a1a;padding:2rem;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .page{max-width:680px;margin:0 auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.10)}

  /* ── Hero banner ── */
  .hero{background:${paintHex};padding:2rem 2rem 1.5rem;position:relative}
  .hero-label{font-size:.7rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:${swatchText};opacity:.7;margin-bottom:.4rem}
  .hero-name{font-size:2rem;font-weight:800;color:${swatchText};line-height:1.1}
  .hero-hex{font-size:.85rem;font-family:monospace;color:${swatchText};opacity:.75;margin-top:.3rem}
  .hero-date{position:absolute;top:1.5rem;right:2rem;font-size:.7rem;color:${swatchText};opacity:.6;text-align:right}
  .hero-logo{position:absolute;bottom:1rem;right:2rem;font-size:.7rem;font-weight:700;letter-spacing:.08em;color:${swatchText};opacity:.5;text-transform:uppercase}

  /* ── Body ── */
  .body{padding:1.75rem 2rem}
  .section{margin-bottom:1.75rem}
  .section-title{font-size:.65rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#999;margin-bottom:.85rem;padding-bottom:.4rem;border-bottom:1px solid #ebebeb}

  /* ── Psychology card ── */
  .psych-card{background:#f9f8f6;border-left:4px solid ${paintHex};border-radius:0 10px 10px 0;padding:1rem 1.25rem}
  .psych-text{font-size:.95rem;line-height:1.65;color:#333;font-style:italic}
  .psych-sources{font-size:.72rem;line-height:1.5;color:#666;margin-top:.65rem}
  .psych-sources a{color:#315c70;text-decoration:underline}

  /* ── Stats grid ── */
  .stats{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin-bottom:1rem}
  .stat{background:#f9f8f6;border-radius:12px;padding:1rem;text-align:center}
  .stat-value{font-size:1.75rem;font-weight:800;color:${paintHex};line-height:1}
  .stat-label{font-size:.7rem;color:#888;margin-top:.35rem;line-height:1.3}

  /* ── Detail rows ── */
  .detail-row{display:flex;justify-content:space-between;align-items:center;padding:.55rem 0;border-bottom:1px solid #f0f0f0;font-size:.875rem}
  .detail-row:last-child{border-bottom:none}
  .detail-label{color:#666}
  .detail-value{font-weight:600;color:#1a1a1a}
  .detail-muted{color:#999;font-size:.78rem;font-weight:400}

  /* ── Checklist ── */
  .checklist{list-style:none}
  .checklist li{display:flex;align-items:flex-start;gap:.75rem;padding:.6rem 0;border-bottom:1px solid #f0f0f0;font-size:.875rem}
  .checklist li:last-child{border-bottom:none}
  .check-box{width:18px;height:18px;border:2px solid #ccc;border-radius:4px;flex-shrink:0;margin-top:.05rem}
  .check-main{font-weight:600;color:#1a1a1a}
  .check-sub{font-size:.75rem;color:#999;margin-top:.1rem}

  /* ── Cost highlight ── */
  .cost-banner{background:${paintHex}18;border:1.5px solid ${paintHex}44;border-radius:12px;padding:1rem 1.25rem;display:flex;justify-content:space-between;align-items:center;margin-top:1rem}
  .cost-label{font-size:.8rem;color:#555;font-weight:500}
  .cost-value{font-size:1.5rem;font-weight:800;color:${paintHex}}

  /* ── Formula note ── */
  .formula{font-size:.72rem;color:#bbb;margin-top:.75rem;line-height:1.5;font-family:monospace}

  /* ── Footer ── */
  .footer{padding:1.25rem 2rem;background:#f9f8f6;border-top:1px solid #ebebeb;display:flex;justify-content:space-between;align-items:center}
  .footer-brand{font-size:.75rem;font-weight:700;letter-spacing:.06em;color:#ccc;text-transform:uppercase}
  .footer-tip{font-size:.7rem;color:#bbb}

  @media print{
    body{background:#fff;padding:0}
    .page{box-shadow:none;border-radius:0}
  }
</style>
</head>
<body>
<div class="page">

  <!-- Hero -->
  <div class="hero">
    <div class="hero-date">Generated<br>${today}</div>
    <div class="hero-label">Paint Color Report</div>
    <div class="hero-name">${colorName}</div>
    <div class="hero-hex">${paintHex.toUpperCase()}</div>
    <div class="hero-logo">Mychromamind.com</div>
  </div>

  <div class="body">

    <!-- Color Psychology -->
    <div class="section">
      <div class="section-title">Color Psychology</div>
      <div class="psych-card">
        <div class="psych-text">"${psychText}"</div>
        ${sourceLinks ? `<div class="psych-sources">Research and further reading: ${sourceLinks}</div>` : ""}
      </div>
    </div>

    <!-- Key Numbers -->
    <div class="section">
      <div class="section-title">Paint Summary</div>
      <div class="stats">
        <div class="stat">
          <div class="stat-value">${net}</div>
          <div class="stat-label">sq ft<br>net wall area</div>
        </div>
        <div class="stat">
          <div class="stat-value">${gals}</div>
          <div class="stat-label">gallons<br>needed</div>
        </div>
        <div class="stat">
          <div class="stat-value">${coats}</div>
          <div class="stat-label">coats<br>planned</div>
        </div>
      </div>
      <div class="cost-banner">
        <div class="cost-label">Estimated Total Cost<br><span style="font-size:.7rem;color:#999">@ $${priceNum} per gallon</span></div>
        <div class="cost-value">$${cost.toFixed(2)}</div>
      </div>
      <div class="formula">
        Gross area: 2 × (${calcInputs.length}ft + ${calcInputs.width}ft) × ${calcInputs.height}ft = ${gross} sq ft
        &nbsp;·&nbsp; Deductions: ${doors} door${doors!==1?"s":""} (−${doorDeduct} sq ft) + ${windows} window${windows!==1?"s":""} (−${windowDeduct} sq ft)
        &nbsp;·&nbsp; Net: ${net} sq ft × ${coats} coats ÷ 350 sq ft/gal = ${gals} gal
      </div>
    </div>

    <!-- Room Dimensions -->
    <div class="section">
      <div class="section-title">Room Details</div>
      <div class="detail-row"><span class="detail-label">Room Dimensions</span><span class="detail-value">${calcInputs.length} ft × ${calcInputs.width} ft × ${calcInputs.height} ft</span></div>
      <div class="detail-row"><span class="detail-label">Gross Wall Area</span><span class="detail-value">${gross} sq ft</span></div>
      <div class="detail-row"><span class="detail-label">Doors <span class="detail-muted">(${doors} × 20 sq ft each)</span></span><span class="detail-value">−${doorDeduct} sq ft</span></div>
      <div class="detail-row"><span class="detail-label">Windows <span class="detail-muted">(${windows} × 15 sq ft each)</span></span><span class="detail-value">−${windowDeduct} sq ft</span></div>
      <div class="detail-row"><span class="detail-label"><strong>Net Paintable Area</strong></span><span class="detail-value"><strong>${net} sq ft</strong></span></div>
    </div>

    <!-- Shopping Checklist -->
    <div class="section">
      <div class="section-title">Shopping Checklist</div>
      <ul class="checklist">
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Paint — ${gals} gallon${gals!==1?"s":""}</div>
            <div class="check-sub">${colorName} · ${paintHex.toUpperCase()} · ${coats} coat${coats!==1?"s":""} · Est. $${cost.toFixed(2)}</div>
          </div>
        </li>
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Primer (if painting over dark/bare walls)</div>
            <div class="check-sub">1 gallon typically covers 200–400 sq ft — check label</div>
          </div>
        </li>
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Roller Kit (frame + cover)</div>
            <div class="check-sub">3/8" nap for smooth walls · 1/2" nap for textured walls</div>
          </div>
        </li>
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Angled Paintbrush (2.5")</div>
            <div class="check-sub">For cutting in edges, corners, and trim</div>
          </div>
        </li>
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Painter's Tape</div>
            <div class="check-sub">For clean edges along trim, ceiling, and baseboards</div>
          </div>
        </li>
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Roller Tray + Liner</div>
            <div class="check-sub">Liner makes clean-up much faster</div>
          </div>
        </li>
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Drop Cloth</div>
            <div class="check-sub">Canvas or plastic to protect floors and furniture</div>
          </div>
        </li>
        <li>
          <div class="check-box"></div>
          <div>
            <div class="check-main">Sandpaper (120-grit) + Spackle</div>
            <div class="check-sub">Patch any holes or imperfections before painting</div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Pro Tips -->
    <div class="section">
      <div class="section-title">Pro Tips for ${colorName}</div>
      <div class="detail-row"><span class="detail-label">🕐 Dry time between coats</span><span class="detail-value">2–4 hours (latex) · 24 hrs (oil)</span></div>
      <div class="detail-row"><span class="detail-label">🌡️ Best temperature to paint</span><span class="detail-value">50°F – 85°F (10°C – 30°C)</span></div>
      <div class="detail-row"><span class="detail-label">💡 Tip</span><span class="detail-value" style="max-width:280px;text-align:right">Test a small patch first and view it in both natural and artificial light before committing.</span></div>
    </div>

  </div><!-- /body -->

  <!-- Footer -->
  <div class="footer">
    <div class="footer-brand">Mychromamind.com</div>
    <div class="footer-tip">Generated with the Room Paint Visualizer</div>
  </div>

</div><!-- /page -->
<script>window.onload = function(){ window.print(); }</script>
</body>
</html>`);
    w.document.close();
  };

  // ── Print store-ready color swatch ────────────────────────────────────────
  const handlePrintSwatch = () => {
    const name      = activeName;
    const psychText = activePsychology;
    const sourceLinks = activeResearchSources.map((source) =>
      `<a href="${escapeHtml(source.url)}" target="_blank" rel="noreferrer">${escapeHtml(source.label)}</a>`
    ).join(" · ");
    const [pr, pg, pb] = hexToRgb(paintHex);
    const luminance = (0.299 * pr + 0.587 * pg + 0.114 * pb) / 255;
    const textColor = luminance > 0.55 ? "#1a1a1a" : "#ffffff";
    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    const w = window.open("", "_blank");
    if (!w) { setPopupBlocked(true); return; }
    w.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Color Swatch — ${name}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:A4;margin:2cm}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#1a1a1a;-webkit-print-color-adjust:exact;print-color-adjust:exact;padding:2rem}
  .page{max-width:600px;margin:0 auto}

  /* Instructions banner */
  .instructions{background:#fffbeb;border:1.5px solid #fbbf24;border-radius:10px;padding:.85rem 1.1rem;margin-bottom:1.75rem;font-size:.82rem;color:#92400e;line-height:1.5}
  .instructions strong{display:block;font-size:.85rem;margin-bottom:.3rem;color:#78350f}

  /* Main swatch — large enough for a spectrophotometer to read */
  .swatch-block{width:100%;height:260px;background:${paintHex};border-radius:14px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.5rem;box-shadow:0 2px 16px rgba(0,0,0,.12)}
  .swatch-name{font-size:2rem;font-weight:800;color:${textColor};text-align:center;letter-spacing:-.01em}
  .swatch-hex{font-family:monospace;font-size:1.1rem;color:${textColor};opacity:.8}
  .swatch-rgb{font-family:monospace;font-size:.85rem;color:${textColor};opacity:.6}

  /* Detail strip */
  .detail-strip{display:flex;gap:1rem;margin:1.25rem 0}
  .detail-card{flex:1;background:#f9f8f6;border-radius:10px;padding:.85rem 1rem;text-align:center}
  .detail-label{font-size:.65rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#999;margin-bottom:.35rem}
  .detail-value{font-size:1rem;font-weight:700;color:#1a1a1a;font-family:monospace}

  /* Psychology */
  .psych{border-left:4px solid ${paintHex};background:#f9f8f6;border-radius:0 10px 10px 0;padding:.85rem 1.1rem;margin-bottom:1.25rem}
  .psych-label{font-size:.65rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#999;margin-bottom:.35rem}
  .psych-text{font-size:.88rem;line-height:1.6;color:#444;font-style:italic}
  .psych-sources{font-size:.68rem;line-height:1.5;color:#666;margin-top:.6rem}
  .psych-sources a{color:#315c70;text-decoration:underline}

  /* Comparison squares */
  .compare-title{font-size:.65rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#999;margin-bottom:.6rem}
  .compare-row{display:flex;gap:.75rem;margin-bottom:1.25rem}
  .compare-sq{flex:1;height:64px;border-radius:8px;position:relative}
  .compare-sq span{position:absolute;bottom:6px;left:0;right:0;text-align:center;font-size:.65rem;font-weight:600;color:#666}

  /* Footer */
  .footer{text-align:center;font-size:.7rem;color:#bbb;margin-top:1rem;padding-top:.75rem;border-top:1px solid #eee}
</style>
</head>
<body>
<div class="page">

  <div class="instructions">
    <strong>📍 How to use this swatch at a paint store</strong>
    Print this page and bring it to any paint counter (Sherwin-Williams, Benjamin Moore, Home Depot, Lowe's). Ask them to scan the large colour block with their <strong>spectrophotometer</strong> to find the closest matching formula in their range. For the most accurate match, use the colour block below — the bigger the sample, the better the reading.
  </div>

  <!-- Main swatch -->
  <div class="swatch-block">
    <div class="swatch-name">${name}</div>
    <div class="swatch-hex">${paintHex.toUpperCase()}</div>
    <div class="swatch-rgb">RGB(${pr}, ${pg}, ${pb})</div>
  </div>

  <!-- Colour values -->
  <div class="detail-strip">
    <div class="detail-card">
      <div class="detail-label">Hex Code</div>
      <div class="detail-value">${paintHex.toUpperCase()}</div>
    </div>
    <div class="detail-card">
      <div class="detail-label">Red</div>
      <div class="detail-value">${pr}</div>
    </div>
    <div class="detail-card">
      <div class="detail-label">Green</div>
      <div class="detail-value">${pg}</div>
    </div>
    <div class="detail-card">
      <div class="detail-label">Blue</div>
      <div class="detail-value">${pb}</div>
    </div>
  </div>

  ${psychText ? `
  <!-- Psychology -->
  <div class="psych">
    <div class="psych-label">Color Psychology</div>
    <div class="psych-text">"${psychText}"</div>
    ${sourceLinks ? `<div class="psych-sources">Research and further reading: ${sourceLinks}</div>` : ""}
  </div>` : ""}

  <!-- Light & dark context squares -->
  <div class="compare-title">How it sits against white &amp; dark walls</div>
  <div class="compare-row">
    <div class="compare-sq" style="background:#ffffff;border:1px solid #e5e5e5">
      <div style="position:absolute;inset:8px;background:${paintHex};border-radius:6px"></div>
      <span style="color:#666">On white</span>
    </div>
    <div class="compare-sq" style="background:${paintHex}">
      <span style="color:${textColor}">Solid block</span>
    </div>
    <div class="compare-sq" style="background:#1a1a1a">
      <div style="position:absolute;inset:8px;background:${paintHex};border-radius:6px"></div>
      <span style="color:#aaa">On dark</span>
    </div>
  </div>

  <div class="footer">
    Generated ${today} · Mychromamind.com — Room Paint Visualizer<br>
    Note: printed colours may differ slightly from on-screen appearance due to printer calibration. Always test a physical paint sample before committing.
  </div>

</div>
<script>window.onload = function(){ window.print(); }</script>
</body>
</html>`);
    w.document.close();
  };

  // Filtered library colors
  const libColors = COLOR_LIST.filter(c =>
    !libSearch || c.name.toLowerCase().includes(libSearch.toLowerCase())
  );

  return (
    <Shell>
      {/* ── Popup-blocked toast ── */}
      {popupBlocked && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-start gap-3 max-w-sm w-full mx-4 px-4 py-3 rounded-2xl bg-zinc-900 text-white shadow-2xl text-sm">
          <span className="text-lg leading-none mt-0.5">🚫</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold">Popup blocked</p>
            <p className="text-zinc-400 text-xs mt-0.5 leading-relaxed">Your browser blocked the print window. Allow popups for this site, then try again.</p>
          </div>
          <button
            onClick={() => setPopupBlocked(false)}
            className="shrink-0 text-zinc-400 hover:text-white transition-colors text-lg leading-none mt-0.5"
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* ── Top ad ── */}
      <div className="w-full max-w-5xl mx-auto px-4 pt-4">
        <AdSlot slotId="2431849490" content={[paintHex]} className="mb-4" />
      </div>

      <main className="w-full max-w-5xl mx-auto px-4 pb-12">
        {/* ── Header ── */}
        <header className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">
            Room Paint Visualizer
          </h1>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 max-w-xl mx-auto">
            Upload a photo of your room or use your live camera — tap any wall to apply paint colors and see how they'll look instantly. 100% in your browser.
          </p>
        </header>

        {/* ── Two-column layout ── */}
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Left: Canvas panel ── */}
          <div className="flex-1 min-w-0">
            {/* Mode tabs */}
            <div className="flex rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-700 mb-4 text-sm font-medium">
              {(["upload","camera"] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-2.5 transition-colors ${mode===m ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-white text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"}`}
                >
                  {m === "upload" ? "📷  Photo Upload" : "🎥  Live Camera"}
                </button>
              ))}
            </div>

            {/* Canvas container */}
            <div
              className="relative rounded-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700"
              style={{ minHeight: 280 }}
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
            >
              {/* Hidden video element for camera feed */}
              <video ref={videoRef} className="hidden" muted playsInline />

              {/* Canvas */}
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                className={`w-full block ${(hasImage || cameraActive) ? "cursor-crosshair" : "cursor-default"}`}
                style={{ display: (hasImage || cameraActive) ? "block" : "none" }}
              />

              {/* Upload placeholder */}
              {!hasImage && !cameraActive && (
                <div
                  className="flex flex-col items-center justify-center py-16 px-6 text-center cursor-pointer"
                  onClick={() => { const i = document.createElement("input"); i.type="file"; i.accept="image/*"; i.onchange=(e)=>{ const f=(e.target as HTMLInputElement).files?.[0]; if(f) handleFile(f); }; i.click(); }}
                >
                  <div className="text-5xl mb-3">🏠</div>
                  <p className="text-zinc-700 dark:text-zinc-300 font-medium">Drag & drop a room photo here</p>
                  <p className="text-sm text-zinc-400 mt-1">or tap to browse files</p>
                  {mode === "camera" && (
                    <button
                      onClick={e => { e.stopPropagation(); startCamera(); }}
                      className="mt-4 px-4 py-2 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-medium hover:opacity-90"
                    >
                      Start Camera
                    </button>
                  )}
                </div>
              )}

              {/* Camera start button when in camera mode but not yet active */}
              {mode === "camera" && !cameraActive && !hasImage && cameraError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 dark:bg-red-900/20 p-4 text-center">
                  <p className="text-red-600 dark:text-red-400 text-sm mb-3">{cameraError}</p>
                  <button onClick={startCamera} className="px-4 py-2 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-medium">
                    Try Again
                  </button>
                </div>
              )}

              {/* Camera active: stop button */}
              {cameraActive && (
                <button
                  onClick={stopCamera}
                  className="absolute top-3 right-3 px-3 py-1 rounded-lg bg-black/50 text-white text-xs font-medium hover:bg-black/70"
                >
                  Stop Camera
                </button>
              )}

              {/* Sampled color indicator */}
              {sampledRgb && (
                <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 rounded-lg px-2 py-1">
                  <div className="w-4 h-4 rounded-full border border-white/60" style={{ background: `rgb(${sampledRgb.join(",")})` }} />
                  <span className="text-white text-xs font-mono">Wall sampled</span>
                </div>
              )}
            </div>

            {/* Hint bar */}
            <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500 text-center">{hint}</p>

            {/* Camera start if mode is camera and no stream yet */}
            {mode === "camera" && !cameraActive && !cameraError && (
              <button
                onClick={startCamera}
                className="w-full mt-3 py-2.5 rounded-xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-medium hover:opacity-90"
              >
                🎥  Start Camera
              </button>
            )}

            {/* Blend controls — only show when a wall is sampled */}
            {sampledRgb && (
              <div className="mt-4 space-y-3 bg-white dark:bg-zinc-800/60 rounded-2xl border border-zinc-100 dark:border-zinc-700 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-400">Blend Controls</h3>
                <div>
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span>Color tolerance</span><span>{tolerance}%</span>
                  </div>
                  <input type="range" min={5} max={95} value={tolerance}
                    onChange={e => setTolerance(+e.target.value)}
                    className="w-full accent-zinc-800 dark:accent-zinc-200" />
                  <p className="text-[10px] text-zinc-400 mt-0.5">Higher = paint covers more similar shades</p>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span>Paint coverage</span><span>{strength}%</span>
                  </div>
                  <input type="range" min={20} max={100} value={strength}
                    onChange={e => setStrength(+e.target.value)}
                    className="w-full accent-zinc-800 dark:accent-zinc-200" />
                  <p className="text-[10px] text-zinc-400 mt-0.5">Higher = more opaque paint effect</p>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <button
                    onClick={() => { setSampledRgb(null); sampledRef.current = null; setHint("Wall selection cleared. Tap a wall to re-sample."); redrawImage(); }}
                    className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 underline"
                  >
                    Clear selection
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-xs font-medium hover:opacity-90 transition-opacity"
                  >
                    💾 Save Image
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Right: Controls ── */}
          <div className="w-full lg:w-80 shrink-0 space-y-5">

            {/* Active paint preview */}
            <div className="rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-700">
              <div className="h-20 transition-colors duration-300" style={{ background: paintHex }} />
              <div className="p-4 bg-white dark:bg-zinc-800/60">
                <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{activeName}</p>
                <p className="text-xs text-zinc-400 font-mono mt-0.5">{paintHex.toUpperCase()}</p>
                {activePsychology && (
                  <p className="mt-2.5 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400 italic border-l-2 pl-3" style={{ borderColor: paintHex }}>
                    "{activePsychology}"
                  </p>
                )}
                {activeResearchSources.length > 0 && (
                  <div className="mt-2 text-[10px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                    <span className="font-medium">Research:</span>{" "}
                    {activeResearchSources.map((source, index) => (
                      <span key={source.url}>
                        {index > 0 && " · "}
                        <a href={source.url} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-zinc-800 dark:hover:text-zinc-200">
                          {index + 1}
                        </a>
                      </span>
                    ))}
                  </div>
                )}
                <button
                  onClick={handlePrintSwatch}
                  className="mt-3 w-full py-2 rounded-lg border border-zinc-200 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 text-xs font-medium hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
                >
                  🎨  Print Swatch for Paint Store
                </button>
              </div>
            </div>

            {/* Custom colour picker */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-400 block mb-2">Custom Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={paintHex}
                  onChange={e => {
                    const hex = e.target.value;
                    // Try to find a name match in grouped colors
                    const match = ALL_GROUPED_COLORS.find(c => c.hex.toLowerCase() === hex.toLowerCase());
                    setPaintHex(hex);
                    setActiveName(match?.name ?? "Custom Color");
                    setActivePsychology(match?.psychology ?? "");
                    setActiveResearchSources(match?.researchSources ?? []);
                  }}
                  className="h-10 w-14 rounded-lg border border-zinc-200 dark:border-zinc-600 cursor-pointer bg-transparent"
                />
                <input
                  type="text"
                  value={paintHex.toUpperCase()}
                  onChange={e => {
                    const v = e.target.value;
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                      const match = ALL_GROUPED_COLORS.find(c => c.hex.toLowerCase() === v.toLowerCase());
                      setPaintHex(v);
                      setActiveName(match?.name ?? "Custom Color");
                      setActivePsychology(match?.psychology ?? "");
                      setActiveResearchSources(match?.researchSources ?? []);
                    }
                  }}
                  className="flex-1 px-3 py-2 text-sm font-mono rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  placeholder="#RRGGBB"
                />
              </div>
            </div>

            {/* ── Category dropdown + swatch grid ── */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-400 block mb-2">Paint Colors</label>
              <select
                value={colorCategory}
                onChange={e => setColorCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-400 mb-3"
              >
                {CATEGORY_KEYS.map(k => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
              <div className="grid grid-cols-4 gap-2">
                {COLOR_GROUPS[colorCategory].map(c => (
                  <button
                    key={c.hex}
                    title={c.name}
                    onClick={() => selectColor(c)}
                    className="group relative aspect-square rounded-xl border-2 transition-all hover:scale-105 focus:outline-none"
                    style={{
                      background: c.hex,
                      borderColor: paintHex.toLowerCase() === c.hex.toLowerCase() ? "#1d1d1d" : "transparent",
                      boxShadow: paintHex.toLowerCase() === c.hex.toLowerCase() ? `0 0 0 3px ${c.hex}55` : undefined,
                    }}
                  >
                    {paintHex.toLowerCase() === c.hex.toLowerCase() && (
                      <span className="absolute inset-0 flex items-center justify-center text-sm drop-shadow" style={{ color: (()=>{ const [r,g,b]=hexToRgb(c.hex); return (0.299*r+0.587*g+0.114*b)/255>0.55?"#000":"#fff"; })() }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[10px] text-zinc-400 text-center">{activeName}</p>
            </div>

            {/* ── Psychology Library (175 colors) ── */}
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
              <button
                onClick={() => setLibOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-800/60 hover:bg-zinc-50 dark:hover:bg-zinc-700/60 transition-colors text-left"
              >
                <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">🎨 Browse Psychology Library <span className="text-zinc-400 font-normal">(175 colors)</span></span>
                <span className="text-zinc-400">{libOpen ? "▲" : "▼"}</span>
              </button>
              {libOpen && (
                <div className="border-t border-zinc-100 dark:border-zinc-700 bg-white dark:bg-zinc-800/40">
                  <div className="p-3">
                    <input
                      type="text"
                      placeholder="Search colors…"
                      value={libSearch}
                      onChange={e => setLibSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-zinc-50 dark:divide-zinc-700/50">
                    {libColors.map(c => (
                      <button
                        key={c.id}
                        onClick={() => selectColor({ name: c.name, hex: c.hex, psychology: c.psychology, researchSources: c.researchSources })}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700/40 transition-colors"
                      >
                        <span
                          className="w-6 h-6 rounded-md shrink-0 border border-black/10"
                          style={{ background: c.hex }}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-zinc-800 dark:text-zinc-200 block truncate">{c.name}</span>
                          <span className={`text-[10px] capitalize px-1.5 py-0.5 rounded-full font-medium ${
                            c.family === "warm"    ? "bg-orange-100 text-orange-700" :
                            c.family === "cool"    ? "bg-blue-100 text-blue-700" :
                            c.family === "growth"  ? "bg-green-100 text-green-700" :
                            c.family === "mystic"  ? "bg-purple-100 text-purple-700" :
                                                     "bg-zinc-100 text-zinc-600"
                          }`}>{c.family}</span>
                        </span>
                        {paintHex.toLowerCase() === c.hex.toLowerCase() && (
                          <span className="text-zinc-400 text-xs">✓</span>
                        )}
                      </button>
                    ))}
                    {libColors.length === 0 && (
                      <p className="px-4 py-6 text-xs text-zinc-400 text-center">No colors match "{libSearch}"</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Paint Calculator (collapsible) ── */}
        <div className="mt-8 rounded-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
          <button
            onClick={() => setCalcOpen(o => !o)}
            className="w-full flex items-center justify-between px-5 py-4 bg-white dark:bg-zinc-800/60 hover:bg-zinc-50 dark:hover:bg-zinc-700/60 transition-colors text-left"
          >
            <span className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm">🧮  Paint Quantity &amp; Cost Estimator</span>
            <span className="text-zinc-400 text-lg">{calcOpen ? "−" : "+"}</span>
          </button>

          {calcOpen && (
            <div className="px-5 pb-6 bg-white dark:bg-zinc-800/40 border-t border-zinc-100 dark:border-zinc-700">
              <p className="text-xs text-zinc-400 mt-4 mb-4">Enter your room dimensions to calculate how much paint you'll need.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {([
                  ["length", "Length (ft)"],
                  ["width",  "Width (ft)"],
                  ["height", "Height (ft)"],
                  ["doors",  "Doors"],
                  ["windows","Windows"],
                  ["coats",  "Coats"],
                ] as [keyof CalcInputs, string][]).map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs text-zinc-500 block mb-1">{label}</label>
                    <input
                      type="number" min={0} step={0.5}
                      value={calcInputs[key]}
                      onChange={e => setCalcInputs(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      placeholder={key === "coats" ? "2" : "0"}
                    />
                  </div>
                ))}
                {/* Price per gallon — user-editable */}
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Price / gallon ($)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                    <input
                      type="number" min={1} step={1}
                      value={pricePerGallon}
                      onChange={e => setPricePerGallon(e.target.value)}
                      className="w-full pl-6 pr-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                      placeholder="55"
                    />
                  </div>
                </div>
              </div>

              {canCalc && (
                <div className="mt-5 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-3">Results</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{calc.net}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">sq ft net wall area</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{calc.gals}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">gallons needed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">${calc.cost}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">est. cost</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-400 text-center mt-3">
                    Formula: 2×(L+W)×H − (doors×20 + windows×15) ÷ {SQFT_PER_GALLON} sq ft/gal × {calcInputs.coats} coats · @${priceNum}/gal
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handlePrint}
                      className="flex-1 py-2 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      🖨️  Full Report &amp; Checklist
                    </button>
                    <button
                      onClick={handlePrintSwatch}
                      className="flex-1 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
                    >
                      🎨  Store Colour Swatch
                    </button>
                  </div>
                </div>
              )}

              {!canCalc && (
                <p className="mt-4 text-xs text-zinc-400 text-center">Enter length, width, and height to see results.</p>
              )}
            </div>
          )}
        </div>

        {/* ── Usage tips ── */}
        <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-100 dark:border-zinc-700 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-3">How it works</h2>
          <ol className="space-y-2 text-xs text-zinc-600 dark:text-zinc-400 list-decimal list-inside">
            <li>Upload a photo of your room or start your camera.</li>
            <li>Tap any wall area on the canvas — this samples the wall colour.</li>
            <li>Choose a paint swatch or enter a custom hex code.</li>
            <li>Adjust <em>tolerance</em> (how much of the wall is selected) and <em>coverage</em> (paint opacity).</li>
            <li>Open the calculator below to estimate gallons and cost.</li>
          </ol>
          <p className="mt-3 text-[10px] text-zinc-400">
            💡 Tip: For best results, ensure your photo is well-lit and the wall colour is fairly uniform. Tap different spots on the wall to re-sample.
          </p>
        </div>
      </main>

      {/* ── Bottom ad ── */}
      <div className="w-full max-w-5xl mx-auto px-4 pb-8">
        <AdSlot slotId="2431849490" content={[paintHex]} className="mt-2" />
      </div>
    </Shell>
  );
}
